import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { Plus, Pencil, Trash2, Users, Target, AlertTriangle } from "lucide-react";
import EntityFormDialog from "./EntityFormDialog";
import TagInput from "./TagInput";

const EMPTY = { name: "", ageRange: "", demographics: "", goals: "", painPoints: "", tags: [] };

const AVATAR_VARIANTS = ["v1", "v2", "v3", "v4"];

const PersonasTab = ({ token, plm }) => {
  const { personas, createPersona, updatePersona, deletePersona, tags: allTags } = plm;

  const [showDlg, setShowDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const tagNames = allTags.map((t) => t.name);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowDlg(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      ageRange: p.ageRange || "",
      demographics: p.demographics || "",
      goals: p.goals || "",
      painPoints: p.painPoints || "",
      tags: p.tags || [],
    });
    setShowDlg(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (editing) {
      await updatePersona(editing.id, form);
    } else {
      await createPersona(form);
    }
    setSaving(false);
    setShowDlg(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this persona?")) return;
    await deletePersona(id);
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div>
      <div className="plm-section-header">
        <h2 className="plm-section-title">
          <span className="icon-wrap"><Users size={20} /></span>
          Personas
        </h2>
        <button className="plm-btn-primary" onClick={openCreate}>
          <Plus size={16} /> New Persona
        </button>
      </div>

      {personas.length === 0 ? (
        <div className="plm-empty">
          <div className="plm-empty-icon"><Users size={28} /></div>
          <div className="plm-empty-title">No personas yet</div>
          <div className="plm-empty-desc">Define your first user persona to understand who you're building for.</div>
          <button className="plm-btn-primary" onClick={openCreate}>
            <Plus size={16} /> Create Persona
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {personas.map((p, idx) => (
            <div key={p.id} className="col-12 col-md-6 col-lg-4">
              <div className="plm-persona-card h-100 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className={`plm-avatar ${AVATAR_VARIANTS[idx % 4]}`}>
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--plm-text)" }}>{p.name}</div>
                      {p.ageRange && (
                        <div style={{ fontSize: "0.78rem", color: "var(--plm-text-muted)" }}>Age: {p.ageRange}</div>
                      )}
                    </div>
                  </div>
                  <div className="d-flex gap-1">
                    <button className="plm-btn-ghost" onClick={() => openEdit(p)}><Pencil size={14} /></button>
                    <button className="plm-btn-ghost danger" onClick={() => handleDelete(p.id)}><Trash2 size={14} /></button>
                  </div>
                </div>

                {p.demographics && (
                  <div style={{ fontSize: "0.82rem", color: "var(--plm-text-muted)", marginBottom: 12 }}>
                    {p.demographics}
                  </div>
                )}

                <div className="plm-persona-meta">
                  {p.goals && (
                    <div className="plm-persona-meta-item">
                      <Target size={14} style={{ color: "var(--plm-success)", flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div className="plm-persona-meta-label">Goals</div>
                        <div style={{ fontSize: "0.82rem" }}>{p.goals}</div>
                      </div>
                    </div>
                  )}
                  {p.painPoints && (
                    <div className="plm-persona-meta-item">
                      <AlertTriangle size={14} style={{ color: "var(--plm-warning)", flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div className="plm-persona-meta-label">Pain Points</div>
                        <div style={{ fontSize: "0.82rem" }}>{p.painPoints}</div>
                      </div>
                    </div>
                  )}
                </div>

                {p.tags?.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-auto pt-3">
                    {p.tags.map((t) => <span key={t} className="plm-tag">{t}</span>)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <EntityFormDialog
        show={showDlg}
        onClose={() => setShowDlg(false)}
        title={editing ? "Edit Persona" : "New Persona"}
        onSubmit={handleSubmit}
        saving={saving}
      >
        <Form.Group className="mb-3">
          <Form.Label>Name *</Form.Label>
          <Form.Control required value={form.name} onChange={(e) => set("name", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Age Range</Form.Label>
          <Form.Control placeholder="e.g. 25-34" value={form.ageRange} onChange={(e) => set("ageRange", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Demographics</Form.Label>
          <Form.Control as="textarea" rows={2} value={form.demographics} onChange={(e) => set("demographics", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Goals</Form.Label>
          <Form.Control as="textarea" rows={2} value={form.goals} onChange={(e) => set("goals", e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Pain Points</Form.Label>
          <Form.Control as="textarea" rows={2} value={form.painPoints} onChange={(e) => set("painPoints", e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Tags</Form.Label>
          <TagInput tags={form.tags} onChange={(t) => set("tags", t)} suggestions={tagNames} />
        </Form.Group>
      </EntityFormDialog>
    </div>
  );
};

export default PersonasTab;
